import React, { useState, useEffect } from 'react';
import { Upload, FileSpreadsheet, X, CheckCircle, AlertCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Label } from './ui/label';
import { useToast } from '../hooks/use-toast';
import * as XLSX from 'xlsx';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Map any header variant (extra spaces, different case) to our canonical field names.
const CANONICAL_HEADERS = {
  portfolio: 'portfolio',
  instrumenttype: 'instrumentType',
  schemecode: 'schemeCode',
  ticker: 'ticker',
  sharename: 'shareName',
  exchange: 'exchange',
  quantity: 'quantity',
  purchaseprice: 'purchasePrice',
  purchasedate: 'purchaseDate',
  currentmarketprice: 'currentMarketPrice',
  sellprice: 'sellPrice',
  selldate: 'sellDate',
  status: 'status',
};

// Normalize a parsed row: trim header whitespace/case and trim string values.
const normalizeRow = (row) => {
  const out = {};
  Object.keys(row).forEach((k) => {
    const key = CANONICAL_HEADERS[k.trim().toLowerCase().replace(/\s+/g, '')] || k.trim();
    const val = row[k];
    out[key] = typeof val === 'string' ? val.trim() : val;
  });
  return out;
};

const ExcelUpload = ({ isOpen, onClose, onSuccess, portfolios = [], defaultPortfolioId = '' }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState([]);
  const [errors, setErrors] = useState([]);
  const [portfolioId, setPortfolioId] = useState(defaultPortfolioId);
  const { toast } = useToast();

  useEffect(() => {
    setPortfolioId(defaultPortfolioId);
  }, [defaultPortfolioId, isOpen]);

  const handleFileChange = async (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    setErrors([]);

    // Parse Excel file
    try {
      const data = await selectedFile.arrayBuffer();
      const workbook = XLSX.read(data);
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet).map(normalizeRow);

      // Validate and transform data
      const { validTransactions, validationErrors } = validateTransactions(jsonData);
      
      setPreview(validTransactions);
      setErrors(validationErrors);

      if (validationErrors.length > 0) {
        toast({
          title: 'Validation Warnings',
          description: `${validationErrors.length} row(s) have issues. Please review.`,
          variant: 'destructive'
        });
      }
    } catch (error) {
      console.error('Error parsing Excel:', error);
      toast({
        title: 'Error',
        description: 'Failed to parse Excel file. Please check the format.',
        variant: 'destructive'
      });
    }
  };

  const validateTransactions = (data) => {
    const validTransactions = [];
    const validationErrors = [];

    data.forEach((row, index) => {
      const errors = [];

      // Normalize status: accept "Sell"/"Sold"/"S" as sold, anything else as held
      const rawStatus = String(row.status || 'held').trim().toLowerCase();
      const status = (rawStatus.startsWith('sol') || rawStatus.startsWith('sel')) ? 'sold' : 'held';

      // Required fields validation
      if (!row.ticker || !row.shareName) {
        errors.push('Missing ticker or share name');
      }
      if (!row.quantity || row.quantity <= 0) {
        errors.push('Invalid quantity');
      }
      if (!row.purchasePrice || row.purchasePrice <= 0) {
        errors.push('Invalid purchase price');
      }
      if (!row.purchaseDate) {
        errors.push('Missing purchase date');
      }
      if (!row.currentMarketPrice || row.currentMarketPrice <= 0) {
        errors.push('Invalid current market price');
      }
      if (status === 'sold' && (!row.sellPrice || row.sellPrice <= 0)) {
        errors.push('Sold row missing sell price');
      }

      if (errors.length > 0) {
        validationErrors.push({
          row: index + 2, // +2 because Excel starts at 1 and header is row 1
          errors: errors.join(', ')
        });
      } else {
        // Transform to match API format
        validTransactions.push({
          portfolioName: row.portfolio ? String(row.portfolio).trim() : '',
          instrumentType: row.instrumentType ? String(row.instrumentType).trim() : 'Equity',
          schemeCode: row.schemeCode ? String(row.schemeCode).trim() : null,
          ticker: String(row.ticker).toUpperCase(),
          shareName: String(row.shareName),
          exchange: row.exchange || 'NSE',
          quantity: parseFloat(row.quantity),
          purchasePrice: parseFloat(row.purchasePrice),
          purchaseDate: formatDate(row.purchaseDate),
          currentMarketPrice: parseFloat(row.currentMarketPrice),
          sellPrice: row.sellPrice ? parseFloat(row.sellPrice) : null,
          sellDate: row.sellDate ? formatDate(row.sellDate) : null,
          status: status
        });
      }
    });

    return { validTransactions, validationErrors };
  };

  const formatDate = (dateValue) => {
    if (!dateValue) return null;
    
    // If it's an Excel date number
    if (typeof dateValue === 'number') {
      const date = XLSX.SSF.parse_date_code(dateValue);
      return `${date.y}-${String(date.m).padStart(2, '0')}-${String(date.d).padStart(2, '0')}`;
    }
    
    // If it's already a string, try to parse it
    const date = new Date(dateValue);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }
    
    return null;
  };

  const handleUpload = async () => {
    if (preview.length === 0) {
      toast({
        title: 'Error',
        description: 'No valid transactions to upload',
        variant: 'destructive'
      });
      return;
    }
    if (!portfolioId && preview.some((t) => !t.portfolioName)) {
      toast({ title: 'Select a portfolio', description: 'Some rows have no "portfolio" column value — choose a target portfolio to import them into.', variant: 'destructive' });
      return;
    }

    setUploading(true);
    try {
      // Resolve portfolios referenced by the optional "portfolio" column,
      // creating any that don't exist yet.
      const nameToId = {};
      portfolios.forEach((p) => { nameToId[p.name.trim().toLowerCase()] = p.id; });
      const neededNames = [...new Set(preview.map((t) => t.portfolioName).filter(Boolean))];
      for (const nm of neededNames) {
        if (!nameToId[nm.toLowerCase()]) {
          const res = await axios.post(`${API}/portfolios`, { name: nm, entityType: 'Individual Investor', panNumber: '' });
          nameToId[nm.toLowerCase()] = res.data.id;
        }
      }

      // Build the transaction list, routing each row to its resolved portfolio
      const toUpload = preview.map((t) => {
        const { portfolioName, ...rest } = t;
        const pid = portfolioName ? nameToId[portfolioName.toLowerCase()] : portfolioId;
        return { ...rest, portfolioId: pid };
      });

      // Single bulk call — saves all rows first (no per-row price lookups); prices enrich in background
      const { data } = await axios.post(`${API}/transactions/bulk`, { transactions: toUpload });
      const successful = data.inserted || 0;
      const failedRows = data.failed || [];

      if (successful > 0) {
        toast({
          title: 'Upload Complete',
          description: `Imported ${successful} of ${data.total} transaction(s).${failedRows.length ? ` ${failedRows.length} failed (see details below). Prices will fill in automatically over the next minute.` : ' Prices will fill in automatically over the next minute.'}`,
        });
        if (failedRows.length) {
          setErrors(failedRows.map((f) => ({ row: f.row, errors: `${f.ticker || ''} — ${f.error}` })));
        }
        onSuccess();
        if (!failedRows.length) handleClose();
      } else {
        toast({
          title: 'Upload Failed',
          description: failedRows.length ? `All ${failedRows.length} rows failed. First error: ${failedRows[0].error}` : 'All transactions failed to upload',
          variant: 'destructive'
        });
        if (failedRows.length) setErrors(failedRows.map((f) => ({ row: f.row, errors: `${f.ticker || ''} — ${f.error}` })));
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: 'Error',
        description: 'Failed to upload transactions',
        variant: 'destructive'
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClose = () => {
    setFile(null);
    setPreview([]);
    setErrors([]);
    onClose();
  };

  const downloadTemplate = () => {
    const template = [
      {
        portfolio: 'Self',
        instrumentType: 'Equity',
        schemeCode: '',
        ticker: 'RELIANCE',
        shareName: 'Reliance Industries Ltd.',
        exchange: 'NSE',
        quantity: 100,
        purchasePrice: 2450.50,
        purchaseDate: '2024-01-15',
        currentMarketPrice: 2890.00,
        sellPrice: '',
        sellDate: '',
        status: 'held'
      },
      {
        portfolio: 'Self',
        instrumentType: 'Equity Mutual Fund',
        schemeCode: '120465',
        ticker: '120465',
        shareName: 'Axis Bluechip Fund - Growth',
        exchange: 'MF',
        quantity: 150.456,
        purchasePrice: 40.50,
        purchaseDate: '2023-03-10',
        currentMarketPrice: 66.61,
        sellPrice: '',
        sellDate: '',
        status: 'held'
      },
      {
        portfolio: 'Spouse',
        instrumentType: 'Bond',
        schemeCode: '',
        ticker: 'IN0020230093',
        shareName: '7.26% GOI 2033',
        exchange: 'NA',
        quantity: 10,
        purchasePrice: 1000.00,
        purchaseDate: '2024-02-10',
        currentMarketPrice: 1025.00,
        sellPrice: '',
        sellDate: '',
        status: 'held'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Transactions');
    XLSX.writeFile(wb, 'portfolio_template.xlsx');
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <FileSpreadsheet className="h-6 w-6 text-orange-600" />
            Upload Excel File
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Target Portfolio */}
          <div className="space-y-2">
            <Label className="text-slate-700">Import into Portfolio *</Label>
            <Select value={portfolioId} onValueChange={setPortfolioId}>
              <SelectTrigger className="border-slate-300 focus:border-orange-500" data-testid="upload-portfolio-select">
                <SelectValue placeholder="Select portfolio" />
              </SelectTrigger>
              <SelectContent>
                {portfolios.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name} ({p.entityType})</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Download Template */}
          <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div>
              <h4 className="font-semibold text-blue-900">Need a template?</h4>
              <p className="text-sm text-blue-700">Download our Excel template to get started</p>
            </div>
            <Button onClick={downloadTemplate} variant="outline" className="border-blue-600 text-blue-600 hover:bg-blue-100">
              Download Template
            </Button>
          </div>

          {/* File Upload */}
          <div className="space-y-2">
            <label className="block text-sm font-medium text-slate-700">
              Select Excel File (.xlsx, .xls)
            </label>
            <div className="flex items-center gap-3">
              <input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileChange}
                className="block w-full text-sm text-slate-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-orange-50 file:text-orange-700
                  hover:file:bg-orange-100"
              />
            </div>
            {file && (
              <p className="text-sm text-slate-600 mt-2">
                Selected: {file.name}
              </p>
            )}
          </div>

          {/* Expected Format */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <h4 className="font-semibold text-slate-700 mb-2">Expected Columns:</h4>
            <div className="text-sm text-slate-600 space-y-1">
              <p>• <strong>portfolio</strong> (optional) - Portfolio name (e.g., Self, Spouse, HUF). Auto-created if new. If blank, the selected portfolio above is used.</p>
              <p>• <strong>instrumentType</strong> (optional) - Equity, ETF, Equity Mutual Fund, Debt Mutual Fund, Bond, FD, or Other (default: Equity)</p>
              <p>• <strong>schemeCode</strong> (optional) - AMFI scheme code for Mutual Funds (enables live NAV auto-update)</p>
              <p>• <strong>ticker</strong> (required) - Stock ticker symbol (or scheme code / ISIN)</p>
              <p>• <strong>shareName</strong> (required) - Full company name</p>
              <p>• <strong>exchange</strong> - NSE or BSE (default: NSE)</p>
              <p>• <strong>quantity</strong> (required) - Number of shares</p>
              <p>• <strong>purchasePrice</strong> (required) - Buy price per share</p>
              <p>• <strong>purchaseDate</strong> (required) - Date format: YYYY-MM-DD</p>
              <p>• <strong>currentMarketPrice</strong> (required) - Current price</p>
              <p>• <strong>sellPrice</strong> (optional) - Sell price if sold</p>
              <p>• <strong>sellDate</strong> (optional) - Sell date if sold</p>
              <p>• <strong>status</strong> - held or sold (default: held)</p>
            </div>
          </div>

          {/* Validation Errors */}
          {errors.length > 0 && (
            <div className="p-4 bg-rose-50 rounded-lg border border-rose-200">
              <div className="flex items-start gap-2">
                <AlertCircle className="h-5 w-5 text-rose-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-rose-900 mb-2">Validation Errors</h4>
                  <div className="space-y-1 text-sm text-rose-700 max-h-40 overflow-y-auto">
                    {errors.map((error) => (
                      <p key={`err-row-${error.row}`}>Row {error.row}: {error.errors}</p>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Preview */}
          {preview.length > 0 && (
            <div className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-emerald-600" />
                <h4 className="font-semibold text-emerald-900">
                  {preview.length} valid transaction(s) ready to upload
                </h4>
              </div>
              <div className="text-sm text-emerald-700 max-h-40 overflow-y-auto">
                {preview.slice(0, 5).map((txn, index) => (
                  <p key={`preview-${txn.ticker}-${txn.purchaseDate}-${index}`}>
                    {txn.ticker} - {txn.quantity} shares @ ₹{txn.purchasePrice}
                  </p>
                ))}
                {preview.length > 5 && (
                  <p className="text-emerald-600 font-semibold">
                    ...and {preview.length - 5} more
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-slate-200">
            <Button 
              type="button" 
              variant="outline" 
              onClick={handleClose}
              className="border-slate-300 hover:bg-slate-100"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleUpload}
              disabled={preview.length === 0 || uploading}
              className="bg-orange-600 hover:bg-orange-700 text-white"
            >
              <Upload className="mr-2 h-4 w-4" />
              {uploading ? 'Uploading...' : `Upload ${preview.length} Transaction(s)`}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ExcelUpload;
